import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TableFooter,
  Paper,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip as MuiTooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TableChart, ShowChart } from '@mui/icons-material';
import { ReportLayout } from '../shared/ReportLayout';
import { ReportFilters } from '../shared/ReportFilters';
import { UsageHeatmap } from './components/UsageHeatmap';
import { useReportData } from '../../../hooks/useReportData';
import { useReportExport } from '../../../hooks/useReportExport';
import { useCanSeeTotalSessions } from '../../../hooks/useCanSeeTotalSessions';
import { formatNumber, formatDate, getDateRangePresets } from '../../../utils/reportHelpers';
import { API_CONFIG } from '../../../config/api';

interface SessionSummary {
  total_sessions: number;
  charged_sessions: number;
  avg_duration_seconds: number;
  avg_duration_minutes: number;
  avg_energy_kwh: number;
  completion_rate: number;
  completed_sessions: number;
  failed_sessions: number;
  total_energy_kwh: number;
  total_revenue: number;
  sessions_per_day: number;
}

interface TimeSeriesData {
  date: string;
  count: number;
  charged_count: number;
  energy: number;
  avg_duration_minutes: number;
  completion_rate: number;
}

interface MethodBreakdown {
  method: string;
  count: number;
  charged_count: number;
  energy: number;
  avg_duration_minutes: number;
}

interface SiteBreakdown {
  site_id: string;
  site_name: string;
  count: number;
  charged_count: number;
  energy: number;
}

interface HourlyPattern {
  hour: number;
  day: number;
  count: number;
}

interface SessionReportData {
  summary: SessionSummary;
  time_series: TimeSeriesData[];
  breakdown_by_method: MethodBreakdown[];
  breakdown_by_site: SiteBreakdown[];
  hourly_pattern: HourlyPattern[];
}

const COLORS = ['#009A75', '#2196F3', '#FFB800', '#FF6B6B', '#9C27B0', '#00BCD4'];

// TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const tableCellWrapSx = {
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  whiteSpace: 'normal',
  verticalAlign: 'top',
};

const tableDetailsSx = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    ...tableCellWrapSx,
    px: { xs: 1, sm: 2 },
    py: { xs: 1, sm: 1.25 },
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
  },
};

const mobileRowCardSx = {
  p: 1.5,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: '#FAFAFA',
};

const mobileLabelSx = {
  fontSize: '0.7rem',
  color: 'text.secondary',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
};

const mobileValueSx = {
  fontSize: '0.875rem',
  fontWeight: 500,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

export const SessionAnalyticsReport: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const presets = getDateRangePresets();
  const canSeeTotalSessions = useCanSeeTotalSessions();

  // Filters state
  const [startDate, setStartDate] = useState(presets.last30Days.start);
  const [endDate, setEndDate] = useState(presets.last30Days.end);
  const [siteId, setSiteId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [chargerId, setChargerId] = useState('');

  // Filter options state
  const [sites, setSites] = useState<Array<{ id: string; name: string }>>([]);
  const [vendors, setVendors] = useState<Array<{ id: string; name: string }>>([]);
  const [chargers, setChargers] = useState<Array<{ id: string; name: string }>>([]);

  // Tab state (0 = table, 1 = charts)
  const [tabValue, setTabValue] = useState(0);

  // Period type state (day/month)
  const [periodType, setPeriodType] = useState<'day' | 'month'>('day');

  // Table sorting state
  const [orderBy, setOrderBy] = useState<keyof TimeSeriesData>('date');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const tokenData = localStorage.getItem('ev_charging_auth_tokens_v2');
    const token = tokenData ? JSON.parse(tokenData).accessToken : null;

    if (!token) {
      console.error('No auth token found');
      return null;
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  // Fetch vendors on mount (vendors list is independent)
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        const response = await fetch(`${API_CONFIG.baseURL}/api/backend/reports/filters/vendors`, {
          headers,
        });
        const data = await response.json();
        setVendors(data.data || []);
      } catch (error) {
        console.error('Error fetching vendors:', error);
      }
    };

    fetchVendors();
  }, []);

  // Fetch sites when vendor changes
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        // Build URL with vendor_id parameter if vendor is selected
        const url = vendorId
          ? `${API_CONFIG.baseURL}/api/backend/reports/filters/sites?vendor_id=${vendorId}`
          : `${API_CONFIG.baseURL}/api/backend/reports/filters/sites`;

        const response = await fetch(url, { headers });
        const data = await response.json();
        setSites(data.data || []);

        // Reset site selection if it's no longer in the filtered list
        if (siteId && !data.data?.some((s: any) => s.id === siteId)) {
          setSiteId('');
        }
      } catch (error) {
        console.error('Error fetching sites:', error);
      }
    };

    fetchSites();
  }, [vendorId]); // Re-fetch when vendor changes

  // Fetch chargers when vendor or site changes
  useEffect(() => {
    const fetchChargers = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        // Build URL with appropriate filter parameters
        let url = `${API_CONFIG.baseURL}/api/backend/reports/filters/chargers`;
        const params = new URLSearchParams();

        if (siteId) {
          params.append('site_id', siteId);
        } else if (vendorId) {
          params.append('vendor_id', vendorId);
        }

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        const response = await fetch(url, { headers });
        const data = await response.json();
        setChargers(data.data || []);

        // Reset charger selection if it's no longer in the filtered list
        if (chargerId && !data.data?.some((c: any) => c.id === chargerId)) {
          setChargerId('');
        }
      } catch (error) {
        console.error('Error fetching chargers:', error);
      }
    };

    fetchChargers();
  }, [vendorId, siteId]); // Re-fetch when vendor or site changes

  // Fetch data
  const { data, loading, error, refetch } = useReportData<SessionReportData>({
    endpoint: '/api/backend/reports/sessions',
    filters: {
      start_date: startDate,
      end_date: endDate,
      initiated_by: 'all',
      group_by: periodType,
      ...(siteId && { site_id: siteId }),
      ...(vendorId && { vendor_id: vendorId }),
      ...(chargerId && { charger_id: chargerId }),
    },
  });

  // Export functionality
  const { exportToCSV, exportToExcel, exporting } = useReportExport();

  const handleExportCSV = () => {
    if (!processedTimeSeriesData) return;

    exportToCSV(
      processedTimeSeriesData,
      'session-analytics',
      [
        { key: 'date', label: periodType === 'day' ? 'Date' : 'Month', format: 'date' },
        ...(canSeeTotalSessions ? [{ key: 'count', label: 'Total Sessions', format: 'number' as const }] : []),
        { key: 'charged_count', label: 'Charged Sessions', format: 'number' },
        { key: 'avg_duration_minutes', label: 'Avg Duration (min)', format: 'number' },
        { key: 'energy', label: 'Energy (kWh)', format: 'number' },
        { key: 'completion_rate', label: 'Completion Rate %', format: 'number' },
      ]
    );
  };

  const handleExportExcel = async () => {
    if (!data) return;

    // Build filters object
    const filters: any = {
      startDate,
      endDate,
    };

    if (siteId) {
      const selectedSite = sites.find(s => s.id === siteId);
      if (selectedSite) {
        filters.site = { id: siteId, name: selectedSite.name };
      }
    }

    if (vendorId) {
      const selectedVendor = vendors.find(v => v.id === vendorId);
      if (selectedVendor) {
        filters.vendor = { id: vendorId, name: selectedVendor.name };
      }
    }

    if (chargerId) {
      const selectedCharger = chargers.find(c => c.id === chargerId);
      if (selectedCharger) {
        filters.charger = { id: chargerId, name: selectedCharger.name };
      }
    }

    await exportToExcel(
      processedTimeSeriesData,
      'session-analytics',
      {
        columns: [
          { key: 'date', label: periodType === 'day' ? translate('reports.table.date', { fallback: 'Date' }) : translate('reports.table.month', { fallback: 'Month' }), format: 'date' },
          ...(canSeeTotalSessions ? [{ key: 'count', label: translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' }), format: 'number' as const }] : []),
          { key: 'charged_count', label: translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' }), format: 'number' },
          { key: 'avg_duration_minutes', label: translate('reports.table.avgDuration', { fallback: 'Avg Duration (min)' }), format: 'number' },
          { key: 'energy', label: translate('reports.table.energyKwh', { fallback: 'Energy (kWh)' }), format: 'number' },
          { key: 'completion_rate', label: translate('reports.table.completionRate', { fallback: 'Completion Rate %' }), format: 'number' },
        ],
        summary: {
          title: translate('reports.sessions.title', { fallback: 'Session Analytics' }),
          data: [
            ...(canSeeTotalSessions ? [{
              label: translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' }),
              value: formatNumber(data.summary.total_sessions, 0)
            }] : []),
            {
              label: translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' }),
              value: formatNumber(data.summary.charged_sessions, 0)
            },
            {
              label: translate('reports.kpi.avgDuration', { fallback: 'Avg Duration' }),
              value: formatNumber(data.summary.avg_duration_minutes, 1) + ' min'
            },
            {
              label: translate('reports.kpi.totalEnergy', { fallback: 'Total Energy' }),
              value: formatNumber(data.summary.total_energy_kwh, 2) + ' kWh'
            },
            {
              label: translate('reports.kpi.completionRate', { fallback: 'Completion Rate' }),
              value: formatNumber(data.summary.completion_rate, 1) + '%'
            },
            {
              label: translate('reports.kpi.sessionsPerDay', { fallback: 'Sessions per Day' }),
              value: formatNumber(data.summary.sessions_per_day, 1)
            },
          ]
        },
        sheetName: translate('reports.tabs.dataTable', { fallback: 'Data' }),
        includeTimestamp: true,
        translate: translate,
        filters: filters
      }
    );
  };

  // Process time series data based on period type
  const processedTimeSeriesData = useMemo(() => {
    if (!data?.time_series) return [];
    if (periodType === 'day') return data.time_series;

    // Aggregate by month
    const monthlyMap = new Map<string, TimeSeriesData>();

    data.time_series.forEach((item) => {
      const month = item.date.substring(0, 7); // YYYY-MM

      if (!monthlyMap.has(month)) {
        monthlyMap.set(month, {
          date: month,
          count: 0,
          charged_count: 0,
          energy: 0,
          avg_duration_minutes: 0,
          completion_rate: 0,
        });
      }

      const entry = monthlyMap.get(month)!;
      entry.count += item.count;
      entry.charged_count += item.charged_count;
      entry.energy += item.energy;
      entry.avg_duration_minutes += item.avg_duration_minutes * item.count; // Weighted average
      entry.completion_rate += item.completion_rate * item.count; // Weighted average
    });

    // Calculate weighted averages
    return Array.from(monthlyMap.values()).map(entry => ({
      ...entry,
      avg_duration_minutes: entry.count > 0 ? entry.avg_duration_minutes / entry.count : 0,
      completion_rate: entry.count > 0 ? entry.completion_rate / entry.count : 0,
    }));
  }, [data?.time_series, periodType]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!processedTimeSeriesData) return [];

    return [...processedTimeSeriesData].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [processedTimeSeriesData, orderBy, order]);

  // Calculate totals for footer
  const totals = useMemo(() => {
    if (!processedTimeSeriesData || processedTimeSeriesData.length === 0) {
      return {
        count: 0,
        charged_count: 0,
        avg_duration_minutes: 0,
        energy: 0,
        completion_rate: 0,
      };
    }

    const totalCount = processedTimeSeriesData.reduce((sum, row) => sum + row.count, 0);
    const totalChargedCount = processedTimeSeriesData.reduce((sum, row) => sum + row.charged_count, 0);
    const totalEnergy = processedTimeSeriesData.reduce((sum, row) => sum + row.energy, 0);
    const weightedDuration = processedTimeSeriesData.reduce((sum, row) => sum + (row.avg_duration_minutes * row.count), 0);
    const weightedCompletionRate = processedTimeSeriesData.reduce((sum, row) => sum + (row.completion_rate * row.count), 0);

    return {
      count: totalCount,
      charged_count: totalChargedCount,
      avg_duration_minutes: totalCount > 0 ? weightedDuration / totalCount : 0,
      energy: totalEnergy,
      completion_rate: totalCount > 0 ? weightedCompletionRate / totalCount : 0,
    };
  }, [processedTimeSeriesData]);

  const handleSort = (property: keyof TimeSeriesData) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Format method names
  const formatMethodName = (method: string) => {
    const methodNames: Record<string, string> = {
      app: translate('reports.methods.app', { fallback: 'Mobile App' }),
      rfid: translate('reports.methods.rfid', { fallback: 'RFID Card' }),
      admin: translate('reports.methods.admin', { fallback: 'Admin' }),
      ocpp: translate('reports.methods.ocpp', { fallback: 'OCPP' }),
      vendor_api: translate('reports.methods.vendorApi', { fallback: 'Vendor API' }),
    };
    return methodNames[method] || method;
  };

  // Prepare pie chart data for initiation methods
  const methodChartData = useMemo(() => {
    if (!data?.breakdown_by_method) return [];
    return data.breakdown_by_method.map((item) => ({
      name: formatMethodName(item.method),
      value: item.count,
      energy: item.energy,
    }));
  }, [data?.breakdown_by_method]);

  const topSiteData = useMemo(() => {
    if (!data?.breakdown_by_site) return [];
    return isMobile ? data.breakdown_by_site.slice(0, 5) : data.breakdown_by_site;
  }, [data?.breakdown_by_site, isMobile]);

  return (
    <ReportLayout
      title={translate('reports.sessions.title', { fallback: 'Session Analytics' })}
      description={translate('reports.sessions.subtitle', { fallback: 'Detailed charging session insights' })}
      filters={
        <ReportFilters
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          siteId={siteId}
          onSiteIdChange={setSiteId}
          vendorId={vendorId}
          onVendorIdChange={setVendorId}
          chargerId={chargerId}
          onChargerIdChange={setChargerId}
          sites={sites}
          vendors={vendors}
          chargers={chargers}
          showSiteFilter={true}
          showVendorFilter={true}
          showChargerFilter={true}
          onRefresh={refetch}
          showGroupByFilter={false}
        />
      }
      exportButtons={{
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        exporting,
        disabled: !data || loading,
      }}
      loading={loading}
      error={error}
    >
      {data && (
        <Box sx={{ maxWidth: '100%', minWidth: 0 }}>
          {/* Tabs */}
          <Card sx={{ mb: 3, overflow: 'hidden', minWidth: 0 }}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 1,
                px: { xs: 1.5, sm: 2 },
                pt: 1.5,
                minWidth: 0,
              }}
            >
              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
                variant={isMobile ? "fullWidth" : "scrollable"}
                scrollButtons={isMobile ? false : "auto"}
                sx={{
                  minWidth: 0,
                  minHeight: { xs: 40, sm: 48 },
                  '& .MuiTab-root': {
                    minWidth: { xs: 0, sm: 90 },
                    minHeight: { xs: 40, sm: 48 },
                    px: { xs: 0.5, sm: 2 },
                    py: { xs: 0.75, sm: 1.5 },
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    textTransform: 'none',
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  },
                }}
              >
                <Tab icon={<TableChart />} iconPosition="start" label={translate('reports.tabs.dataTable', { fallback: 'Data Table' })} />
                <Tab icon={<ShowChart />} iconPosition="start" label={translate('reports.tabs.charts', { fallback: 'Charts' })} />
              </Tabs>

              {/* Period Toggle */}
              {tabValue === 0 && (
                <ToggleButtonGroup
                  value={periodType}
                  exclusive
                  onChange={(_, newValue) => newValue && setPeriodType(newValue)}
                  size="small"
                  sx={{
                    mb: 1,
                    alignSelf: { xs: 'stretch', sm: 'center' },
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiToggleButton-root': {
                      flex: { xs: 1, sm: 'unset' },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      py: { xs: 0.75, sm: 0.5 },
                    },
                  }}
                >
                  <ToggleButton value="day">{translate('reports.groupBy.day', { fallback: 'Day' })}</ToggleButton>
                  <ToggleButton value="month">{translate('reports.groupBy.month', { fallback: 'Month' })}</ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>

            {/* Data Table Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#1A1A1A',
                    mb: 2,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {translate('reports.dailyBreakdown', { fallback: 'Daily Breakdown' })} ({sortedData.length} {translate('reports.days', { fallback: 'days' })})
                </Typography>

                {isMobile ? (
                  <Box sx={{ display: 'grid', gap: 1.25 }}>
                    <Paper variant="outlined" sx={mobileRowCardSx}>
                      <Typography sx={{ ...mobileLabelSx, mb: 1 }}>
                        {translate('reports.table.total', { fallback: 'Total' })}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                        {canSeeTotalSessions && (
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(totals.count, 0)}</Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatNumber(totals.charged_count, 0)}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.table.avgDuration', { fallback: 'Avg Duration (min)' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatNumber(totals.avg_duration_minutes, 1)} min</Typography>
                        </Box>
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.table.energyKwh', { fallback: 'Energy (kWh)' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatNumber(totals.energy, 2)} kWh</Typography>
                        </Box>
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.table.completionRate', { fallback: 'Completion Rate %' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatNumber(totals.completion_rate, 1)}%</Typography>
                        </Box>
                      </Box>
                    </Paper>

                    {sortedData.map((row) => (
                      <Paper key={row.date} variant="outlined" sx={mobileRowCardSx}>
                        <Typography sx={{ ...mobileLabelSx, mb: 0.75 }}>
                          {periodType === 'day'
                            ? translate('reports.table.date', { fallback: 'Date' })
                            : translate('reports.table.month', { fallback: 'Month' })
                          }
                        </Typography>
                        <Typography sx={{ ...mobileValueSx, fontWeight: 700, mb: 1 }}>
                          {periodType === 'day' ? formatDate(row.date, 'long') : row.date}
                        </Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                          {canSeeTotalSessions && (
                            <Box>
                              <Typography sx={mobileLabelSx}>{translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}</Typography>
                              <Typography sx={mobileValueSx}>{formatNumber(row.count, 0)}</Typography>
                            </Box>
                          )}
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(row.charged_count, 0)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.avgDuration', { fallback: 'Avg Duration (min)' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(row.avg_duration_minutes, 1)} min</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.energyKwh', { fallback: 'Energy (kWh)' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(row.energy, 2)} kWh</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.completionRate', { fallback: 'Completion Rate %' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(row.completion_rate, 1)}%</Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E0E0E0', maxHeight: 600 }}>
                      <Table stickyHeader sx={tableDetailsSx}>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <TableSortLabel
                                active={orderBy === 'date'}
                                direction={orderBy === 'date' ? order : 'asc'}
                                onClick={() => handleSort('date')}
                              >
                                {periodType === 'day'
                                  ? translate('reports.table.date', { fallback: 'Date' })
                                  : translate('reports.table.month', { fallback: 'Month' })
                                }
                              </TableSortLabel>
                            </TableCell>
                            {canSeeTotalSessions && (
                              <TableCell align="right">
                                <TableSortLabel
                                  active={orderBy === 'count'}
                                  direction={orderBy === 'count' ? order : 'asc'}
                                  onClick={() => handleSort('count')}
                                >
                                  {translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}
                                </TableSortLabel>
                              </TableCell>
                            )}
                            <TableCell align="right">
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                <TableSortLabel
                                  active={orderBy === 'charged_count'}
                                  direction={orderBy === 'charged_count' ? order : 'asc'}
                                  onClick={() => handleSort('charged_count')}
                                >
                                  {translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}
                                </TableSortLabel>
                                <MuiTooltip
                                  title={translate('reports.kpi.chargedSessionsTooltip', {
                                    fallback: 'Sessions that actually delivered energy (kWh > 0).',
                                  })}
                                  arrow
                                  placement="top"
                                >
                                  <Box component="span" sx={{ cursor: 'help', color: '#9E9E9E' }}>ⓘ</Box>
                                </MuiTooltip>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'avg_duration_minutes'}
                                direction={orderBy === 'avg_duration_minutes' ? order : 'asc'}
                                onClick={() => handleSort('avg_duration_minutes')}
                              >
                                {translate('reports.table.avgDuration', { fallback: 'Avg Duration (min)' })}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'energy'}
                                direction={orderBy === 'energy' ? order : 'asc'}
                                onClick={() => handleSort('energy')}
                              >
                                {translate('reports.table.energyKwh', { fallback: 'Energy (kWh)' })}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'completion_rate'}
                                direction={orderBy === 'completion_rate' ? order : 'asc'}
                                onClick={() => handleSort('completion_rate')}
                              >
                                {translate('reports.table.completionRate', { fallback: 'Completion Rate %' })}
                              </TableSortLabel>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedData.map((row) => (
                            <TableRow key={row.date}>
                              <TableCell>{periodType === 'day' ? formatDate(row.date, 'long') : row.date}</TableCell>
                              {canSeeTotalSessions && (
                                <TableCell align="right">{formatNumber(row.count, 0)}</TableCell>
                              )}
                              <TableCell align="right">{formatNumber(row.charged_count, 0)}</TableCell>
                              <TableCell align="right">{formatNumber(row.avg_duration_minutes, 1)}</TableCell>
                              <TableCell align="right">{formatNumber(row.energy, 2)}</TableCell>
                              <TableCell align="right">{formatNumber(row.completion_rate, 1)}%</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableFooter>
                          <TableRow
                            sx={{
                              '& td': {
                                position: 'sticky',
                                bottom: 0,
                                backgroundColor: '#F5F5F5',
                                borderTop: '2px solid #E0E0E0',
                                fontWeight: 600,
                                zIndex: 2,
                              }
                            }}
                          >
                            <TableCell>{translate('reports.table.total', { fallback: 'Total' })}</TableCell>
                            {canSeeTotalSessions && (
                              <TableCell align="right">{formatNumber(totals.count, 0)}</TableCell>
                            )}
                            <TableCell align="right">{formatNumber(totals.charged_count, 0)}</TableCell>
                            <TableCell align="right">{formatNumber(totals.avg_duration_minutes, 1)}</TableCell>
                            <TableCell align="right">{formatNumber(totals.energy, 2)}</TableCell>
                            <TableCell align="right">{formatNumber(totals.completion_rate, 1)}%</TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Charts Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 2, sm: 3 }, minWidth: 0 }}>
                {/* Sessions Over Time Chart */}
                <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, mb: 3, mt: 3, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#1A1A1A',
                      mb: 3,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {translate('reports.charts.sessionsOverTime', { fallback: 'Sessions Over Time' })}
                  </Typography>
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <LineChart data={processedTimeSeriesData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(date) => periodType === 'month' ? date : formatDate(date)}
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        stroke="#606060"
                        interval={isMobile ? 'preserveStartEnd' : 0}
                        minTickGap={isMobile ? 24 : 12}
                      />
                      <YAxis
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        stroke="#606060"
                        tickFormatter={(value) => formatNumber(value, 0)}
                        width={isMobile ? 42 : 54}
                      />
                      <Tooltip
                        formatter={(value: number, name: string) => {
                          if (name === 'energy') return `${formatNumber(value, 2)} kWh`;
                          if (name === 'completion_rate') return `${formatNumber(value, 1)}%`;
                          if (name === 'avg_duration_minutes') return `${formatNumber(value, 0)} min`;
                          return formatNumber(value, 0);
                        }}
                        labelFormatter={(date) => periodType === 'month' ? date : formatDate(date, 'long')}
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E0E0E0',
                          borderRadius: 8,
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
                      <Line
                        type="monotone"
                        dataKey={canSeeTotalSessions ? 'count' : 'charged_count'}
                        stroke="#009A75"
                        strokeWidth={isMobile ? 2 : 3}
                        dot={{ r: isMobile ? 3 : 4, fill: '#009A75' }}
                        name={canSeeTotalSessions ? 'Sessions' : 'Charged Sessions'}
                      />
                      <Line
                        type="monotone"
                        dataKey="completion_rate"
                        stroke="#2196F3"
                        strokeWidth={isMobile ? 1.75 : 2}
                        dot={{ r: isMobile ? 2.5 : 3, fill: '#2196F3' }}
                        name="Completion Rate %"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 3, mb: 3, minWidth: 0 }}>
                  {/* Sessions by Initiation Method Chart */}
                  <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#1A1A1A',
                        mb: 3,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {translate('reports.charts.sessionsByMethod', { fallback: 'Sessions by Initiation Method' })}
                    </Typography>
                    <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                      <PieChart>
                        <Pie
                          data={methodChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={isMobile ? false : ({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={isMobile ? 70 : 80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {methodChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: number, name: string) => {
                            if (name === 'energy') return `${formatNumber(value, 2)} kWh`;
                            return formatNumber(value, 0);
                          }}
                          contentStyle={{
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #E0E0E0',
                            borderRadius: 8,
                          }}
                        />
                        <Legend
                          verticalAlign={isMobile ? 'bottom' : 'top'}
                          align="center"
                          wrapperStyle={{ fontSize: isMobile ? 10 : 12 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Sessions by Site Chart */}
                  <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#1A1A1A',
                        mb: 3,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {translate('reports.charts.sessionsBySite', { fallback: 'Sessions by Site' })}
                    </Typography>
                    {topSiteData && topSiteData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={isMobile ? 300 : 300}>
                        {isMobile ? (
                          <BarChart
                            data={topSiteData}
                            layout="vertical"
                            margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                            <XAxis
                              type="number"
                              tick={{ fontSize: 10 }}
                              stroke="#606060"
                              tickFormatter={(value) => formatNumber(value, 0)}
                            />
                            <YAxis
                              type="category"
                              dataKey="site_name"
                              width={90}
                              tick={{ fontSize: 10 }}
                              stroke="#606060"
                              tickFormatter={(value) => String(value).slice(0, 14)}
                            />
                            <Tooltip
                              formatter={(value: number, name: string) => {
                                if (name === 'energy') return `${formatNumber(value, 2)} kWh`;
                                return formatNumber(value, 0);
                              }}
                              contentStyle={{
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #E0E0E0',
                                borderRadius: 8,
                              }}
                            />
                            <Bar dataKey={canSeeTotalSessions ? 'count' : 'charged_count'} fill="#009A75" name={canSeeTotalSessions ? 'Sessions' : 'Charged Sessions'} radius={[0, 6, 6, 0]} />
                          </BarChart>
                        ) : (
                          <BarChart data={topSiteData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                            <XAxis
                              dataKey="site_name"
                              tick={{ fontSize: 11 }}
                              stroke="#606060"
                              angle={-45}
                              textAnchor="end"
                              height={100}
                              interval={0}
                            />
                            <YAxis
                              tick={{ fontSize: 12 }}
                              stroke="#606060"
                              tickFormatter={(value) => formatNumber(value, 0)}
                            />
                            <Tooltip
                              formatter={(value: number, name: string) => {
                                if (name === 'energy') return `${formatNumber(value, 2)} kWh`;
                                return formatNumber(value, 0);
                              }}
                              contentStyle={{
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #E0E0E0',
                                borderRadius: 8,
                              }}
                            />
                            <Bar dataKey={canSeeTotalSessions ? 'count' : 'charged_count'} fill="#009A75" name={canSeeTotalSessions ? 'Sessions' : 'Charged Sessions'} radius={[8, 8, 0, 0]} />
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    ) : (
                      <Box sx={{ py: 8, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          No site data available
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Hourly Usage Heatmap */}
                <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#1A1A1A',
                      mb: 3,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {translate('reports.charts.hourlyUsagePattern', { fallback: 'Hourly Usage Pattern' })}
                  </Typography>
                  <UsageHeatmap data={data.hourly_pattern} loading={loading} />
                </Box>
              </Box>
            </TabPanel>
          </Card>
        </Box>
      )}
    </ReportLayout>
  );
};
